/**
 * AI CONTEXT: mobile-first - Product detail page
 * Journal: J-20250809-01
 * 
 * This component implements the product detail view with:
 * - Mobile-first responsive design
 * - Product information display
 * - Shopping cart integration
 * - Quantity selection
 * - Touch-friendly interface elements
 * 
 * Architecture decisions:
 * - Use comprehensive product information display
 * - Implement quantity selection
 * - Provide clear pricing and entry information
 * - Follow mobile-first design principles
 * 
 * Future considerations:
 * - Add product image gallery
 * - Implement product reviews
 * - Add related products
 * - Consider product variants
 */

import React, { useMemo, useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ShoppingBag, Star, ArrowLeft, Minus, Plus } from 'lucide-react';
import { useCart } from '../contexts/CartContext';
import { useProducts } from '../contexts/ProductContext';
// import { getAssetUrl } from '../utils/assetUtils';
import S3Image from '../components/S3Image';

const ProductDetail = () => {
  const { id } = useParams();
  const { addToCart } = useCart();
  const { products, loading } = useProducts();
  const [quantity, setQuantity] = useState(1);

  // AI CONTEXT: data-fetching - Resolve product by id from context (API-backed)
  // Reasoning: Ensure Product Detail reflects selected item from Store/Admin-managed data
  // Constraints: Handle direct loads (wait for context to fetch); guard missing optional fields
  const product = useMemo(() => products.find(p => String(p.id) === String(id)), [products, id]);

  // AI CONTEXT: ux - Handle quantity changes
  // Reasoning: Allow users to select product quantity
  // Constraints: Must prevent negative quantities
  // Future: Add stock limit checking

  const handleQuantityChange = (newQuantity) => {
    if (newQuantity >= 1) {
      setQuantity(newQuantity);
    }
  };

  // AI CONTEXT: ux - Handle add to cart
  // Reasoning: Provide immediate feedback for cart actions
  // Constraints: Must integrate with cart context
  // Future: Add stock checking and quantity validation

  const handleAddToCart = () => {
    if (product) addToCart({ ...product, quantity });
  };

  // AI CONTEXT: ux - Render star ratings
  // Reasoning: Provide visual product ratings
  // Constraints: Must handle decimal ratings
  // Future: Add detailed review system

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    for (let i = 0; i < fullStars; i++) {
      stars.push(<Star key={i} className="h-4 w-4 fill-yellow-400 text-yellow-400" />);
    }

    if (hasHalfStar) {
      stars.push(<Star key="half" className="h-4 w-4 fill-yellow-400 text-yellow-400" />);
    }

    const emptyStars = 5 - Math.ceil(rating);
    for (let i = 0; i < emptyStars; i++) {
      stars.push(<Star key={`empty-${i}`} className="h-4 w-4 text-gray-300" />);
    }

    return stars;
  };

  useEffect(() => {
    if (!product) return;
    const siteBase = 'https://tolberttactical.com';
    const canonicalUrl = `${siteBase}/product/${encodeURIComponent(String(product.id))}`;

    // Canonical link
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', canonicalUrl);

    // JSON-LD Product schema
    const ld = {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: product.name,
      image: Array.isArray(product.images) && product.images.length > 0 ? product.images.map(i => (i.url || i)) : [product.image || `${siteBase}/files/Logo.png`],
      description: product.description || '',
      sku: String(product.id),
      brand: {
        '@type': 'Brand',
        name: 'Tolbert Tactical'
      },
      aggregateRating: product.rating ? {
        '@type': 'AggregateRating',
        ratingValue: Number(product.rating),
        reviewCount: Number(product.reviews || 0)
      } : undefined,
      offers: {
        '@type': 'Offer',
        url: canonicalUrl,
        priceCurrency: 'USD',
        price: Number(product.price || 0),
        availability: product.inStock ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
        itemCondition: 'https://schema.org/NewCondition'
      }
    };

    // Remove previous script if any for PDP to prevent duplicates
    const existing = document.getElementById('pdp-jsonld');
    if (existing) existing.remove();

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'pdp-jsonld';
    script.text = JSON.stringify(ld);
    document.head.appendChild(script);

    return () => {
      // Optional cleanup: do not remove canonical for SPA navigation unless replaced elsewhere
      const s = document.getElementById('pdp-jsonld');
      if (s) s.remove();
    };
  }, [product]);

  if (loading && !product) {
    return (
      <div className="min-h-screen bg-theme-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center py-24">
            <ShoppingBag className="h-16 w-16 text-theme-silver mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-theme-silver mb-2">Loading product…</h3>
            <p className="text-theme-silver-light">Please wait.</p>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-theme-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center py-24">
            <ShoppingBag className="h-16 w-16 text-theme-silver mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-theme-silver mb-2">Product not found</h3>
            <p className="text-theme-silver-light mb-6">The product you are looking for does not exist.</p>
            <Link to="/store" className="btn-primary">Back to Store</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-theme-black">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <div className="mb-6">
          <Link
            to="/store"
            className="inline-flex items-center text-custom-orange hover:text-custom-orange/80 font-medium"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Store
          </Link>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Product Image */}
          <div className="glass-card-dark p-8">
            <div className="aspect-square bg-glass-white/10 rounded-lg flex items-center justify-center overflow-hidden">
              <S3Image
                s3KeyOrUrl={product.image || '/files/Logo.png'}
                alt={product.name}
                className="w-full h-full max-w-full max-h-full object-contain p-4"
              />
            </div>
          </div>

          {/* Product Info */}
          <div className="space-y-6">
            <div>
              <div className="flex items-center justify-between mb-4">
                <h1 className="text-3xl font-bold text-theme-silver">{product.name}</h1>
                <span className={`tier-badge tier-${(product.tier || 'Bronze').toLowerCase()}`}>{product.tier} Tier</span>
              </div>
              
              <div className="flex items-center space-x-4 mb-4">
                <div className="flex items-center space-x-1">
                  {renderStars(product.rating || 0)}
                  <span className="text-sm text-theme-silver-light ml-2">
                    {(product.rating || 0)} ({product.reviews || 0} reviews)
                  </span>
                </div>
                <span className="text-sm text-theme-silver-light">
                  {product.inStock ? 'In Stock' : 'Out of Stock'}
                </span>
              </div>
            </div>

            {/* Price and Entries */}
            <div className="glass-card-dark p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="text-3xl font-bold">
                  {product.salesPrice ? (
                    <div className="flex items-center space-x-3">
                      <span className="text-custom-orange line-through">${Number(product.price).toLocaleString()}</span>
                      <span className="text-theme-silver">${Number(product.salesPrice).toLocaleString()}</span>
                    </div>
                  ) : (
                    <span className="text-theme-silver">${Number(product.price).toLocaleString()}</span>
                  )}
                </div>
                <span className="text-lg text-custom-orange font-semibold">
                  {product.entries} giveaway entry{product.entries > 1 ? 's' : ''}
                </span>
              </div>
              
              <p className="text-theme-silver-light mb-4">{product.description}</p>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-theme-silver-light">Quantity:</span>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleQuantityChange(quantity - 1)}
                      className="p-1 rounded border border-theme-silver/20 hover:bg-glass-white/10"
                    >
                      <Minus className="h-4 w-4 text-theme-silver" />
                    </button>
                    <span className="w-12 text-center text-theme-silver font-medium">{quantity}</span>
                    <button
                      onClick={() => handleQuantityChange(quantity + 1)}
                      className="p-1 rounded border border-theme-silver/20 hover:bg-glass-white/10"
                    >
                      <Plus className="h-4 w-4 text-theme-silver" />
                    </button>
                  </div>
                </div>
                
                <div className="text-right">
                  <p className="text-sm text-theme-silver-light">Total:</p>
                  <p className="text-xl font-bold text-theme-silver">${(Number(product.price) * quantity).toFixed(2)}</p>
                  <p className="text-sm text-custom-orange">
                    {product.entries * quantity} total entries
                  </p>
                </div>
              </div>
            </div>

            {/* Add to Cart */}
            <button
              onClick={handleAddToCart}
              disabled={!product.inStock}
              className="w-full btn-primary text-lg py-4 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {product.inStock ? 'Add to Cart' : 'Out of Stock'}
            </button>

            {/* Features */}
            {Array.isArray(product.features) && product.features.length > 0 && (
              <div className="glass-card-dark p-6">
                <h3 className="text-lg font-semibold text-theme-silver mb-4">Features</h3>
                <ul className="space-y-2">
                  {product.features.map((feature, index) => (
                    <li key={index} className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-custom-orange rounded-full"></div>
                      <span className="text-theme-silver-light">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Specifications */}
            {product.specifications && (
              <div className="glass-card-dark p-6">
                <h3 className="text-lg font-semibold text-theme-silver mb-4">Specifications</h3>
                <div className="space-y-2">
                  {Object.entries(product.specifications).map(([key, value]) => (
                    <div key={key} className="flex justify-between">
                      <span className="text-theme-silver-light">{key}:</span>
                      <span className="text-theme-silver font-medium">{String(value)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail; 